import { useState, useMemo } from 'react';
import { User, Mail, Briefcase, Shield } from 'lucide-react';
import { CreateExternalUserDto, UpdateExternalUserDto, ROLE_TYPE_LABELS } from '../../types/external-user.types';
import { PermissionsSelector } from '../../components/PermissionsSelector';
import { getPermissionsForUserType, DEFAULT_PERMISSIONS_BY_ROLE } from '@/features/external-users/constants/permissions';
import { useSociety } from '../../../societies/hooks/useSocieties';
import { useEffectiveConfiguration } from '../../../societies/hooks/useSocietyConfiguration';
import { useIsWarrantyPolicyOwner } from '@/features/warranties/hooks/useIsWarrantyPolicyOwner';
import { SocietySelector } from './SocietySelector';
import { PhoneInput } from '../../../../shared/PhoneInput';
import { AddressAutocomplete } from '../../../../shared/AddressAutocomplete';
import type { LocationResult } from '../../../../../../services/geocoding.service';

interface ExternalUserFormProps {
  initialData?: Partial<UpdateExternalUserDto> & { societyId?: string };
  onSubmit: (data: CreateExternalUserDto | UpdateExternalUserDto) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isEdit?: boolean;
}

export const ExternalUserForm: React.FC<ExternalUserFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    societyId: initialData?.societyId || '',
    civility: initialData?.civility || '',
    firstname: initialData?.firstname || '',
    lastname: initialData?.lastname || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    address: initialData?.address || '',
    postalCode: initialData?.postalCode || '',
    city: initialData?.city || '',
    position: initialData?.position || '',
    roleType: (initialData as any)?.roleType || 'USER',
    permissions: initialData?.permissions || DEFAULT_PERMISSIONS_BY_ROLE['USER'] || [],
  });

  const [showCustomPermissions, setShowCustomPermissions] = useState(false);

  // Récupérer la société et sa configuration effective pour le filtrage des permissions
  const { data: selectedSociety } = useSociety(formData.societyId || undefined);
  const { data: effectiveConfig } = useEffectiveConfiguration(formData.societyId || undefined);
  const isWarrantyPolicyOwner = useIsWarrantyPolicyOwner(formData.societyId || undefined);

  // Permissions disponibles pour les utilisateurs externes (filtrées par vendorType + options société)
  const availablePermissions = useMemo(() => {
    const sid = formData.societyId;
    return getPermissionsForUserType('EXTERNAL', selectedSociety?.vendorType, effectiveConfig ? {
      deliveryProofCreate: effectiveConfig.deliveryProofCreate,
      isWarrantyPolicyOwner,
      defineProvision: effectiveConfig.defineProvision,
      guarantorId: effectiveConfig.guarantorId,
      isTransferPrice: effectiveConfig.isTransferPrice,
      billedId: effectiveConfig.billedId,
      ownSocietyId: sid,
    } : undefined);
  }, [formData.societyId, selectedSociety?.vendorType, effectiveConfig?.deliveryProofCreate, isWarrantyPolicyOwner, effectiveConfig?.defineProvision, effectiveConfig?.guarantorId, effectiveConfig?.isTransferPrice, effectiveConfig?.billedId]);

  // Permissions par défaut selon le rôle
  const defaultPermissionsForRole = useMemo(() => {
    return DEFAULT_PERMISSIONS_BY_ROLE[formData.roleType] || [];
  }, [formData.roleType]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Si le roleType change et qu'on n'est pas en mode personnalisé, mettre à jour les permissions
      if (field === 'roleType' && !showCustomPermissions) {
        updated.permissions = DEFAULT_PERMISSIONS_BY_ROLE[value as 'ADMIN' | 'USER'] || [];
      }

      return updated;
    });
  };

  const handlePermissionsChange = (permissions: string[]) => {
    setFormData((prev) => ({ ...prev, permissions }));
  };

  const handleResetPermissions = () => {
    setFormData((prev) => ({
      ...prev,
      permissions: defaultPermissionsForRole,
    }));
    setShowCustomPermissions(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData: any = { ...formData };
    
    // Nettoyer les champs optionnels vides
    if (!submitData.civility) delete submitData.civility;
    if (!submitData.phone) delete submitData.phone;
    if (!submitData.address) delete submitData.address;
    if (!submitData.postalCode) delete submitData.postalCode;
    if (!submitData.city) delete submitData.city;
    if (!submitData.position) delete submitData.position;

    // En mode édition, supprimer societyId (non modifiable)
    if (isEdit) {
      delete submitData.societyId;
      delete submitData.roleType;
    }

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="tw-space-y-6">
      {/* Société (uniquement en création) */}
      {!isEdit && (
        <SocietySelector
          value={formData.societyId}
          onChange={(societyId) => handleChange('societyId', societyId)}
          error={!formData.societyId ? 'Société requise' : undefined}
        />
      )}

      {/* Civilité + Nom + Prénom */}
      <div className="tw-grid tw-grid-cols-3 tw-gap-4">
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            Civilité
          </label>
          <select
            value={formData.civility}
            onChange={(e) => handleChange('civility', e.target.value)}
            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-affinicar-primary focus:tw-border-blue-500"
          >
            <option value="">-</option>
            <option value="M.">M.</option>
            <option value="Mme">Mme</option>
          </select>
        </div>

        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            Prénom *
          </label>
          <div className="tw-relative">
            <User className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" />
            <input
              type="text"
              value={formData.firstname}
              onChange={(e) => handleChange('firstname', e.target.value.toUpperCase())}
              className="tw-w-full tw-pl-10 tw-pr-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-affinicar-primary focus:tw-border-blue-500"
              required
            />
          </div>
        </div>

        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            Nom *
          </label>
          <div className="tw-relative">
            <User className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" />
            <input
              type="text"
              value={formData.lastname}
              onChange={(e) => handleChange('lastname', e.target.value.toUpperCase())}
              className="tw-w-full tw-pl-10 tw-pr-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-affinicar-primary focus:tw-border-blue-500"
              required
            />
          </div>
        </div>
      </div>

      {/* Email + Téléphone */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            Email *
          </label>
          <div className="tw-relative">
            <Mail className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" />
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="tw-w-full tw-pl-10 tw-pr-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-affinicar-primary focus:tw-border-blue-500"
              required
            />
          </div>
        </div>

        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            Téléphone
          </label>
          <PhoneInput
            value={formData.phone}
            onChange={(value) => handleChange('phone', value)}
            label=""
          />
        </div>
      </div>

      {/* Adresse */}
      <div>
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
          Adresse
        </label>
        <AddressAutocomplete
          value={formData.address}
          onLocationSelect={(location: LocationResult) => {
            handleChange('address', location.address);
            handleChange('postalCode', location.postalCode || '');
            handleChange('city', location.city || '');
          }}
          onChange={(value) => handleChange('address', value)}
          placeholder="Rechercher l'adresse..."
          searchType="address"
        />
      </div>

      {/* Code postal + Ville */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            Code postal
          </label>
          <input
            type="text"
            value={formData.postalCode}
            onChange={(e) => handleChange('postalCode', e.target.value)}
            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-affinicar-primary focus:tw-border-blue-500"
          />
        </div>

        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            Ville
          </label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => handleChange('city', e.target.value)}
            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-affinicar-primary focus:tw-border-blue-500"
          />
        </div>
      </div>

      {/* Poste */}
      <div>
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
          Poste
        </label>
        <div className="tw-relative">
          <Briefcase className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" />
          <input
            type="text"
            value={formData.position}
            onChange={(e) => handleChange('position', e.target.value)}
            className="tw-w-full tw-pl-10 tw-pr-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-affinicar-primary focus:tw-border-blue-500"
            placeholder="ex: Responsable commercial"
          />
        </div>
      </div>

      {/* Rôle (uniquement en création) */}
      {!isEdit && (
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            <Shield className="tw-inline tw-w-4 tw-h-4 tw-mr-1" />
            Rôle *
          </label>
          <select
            value={formData.roleType}
            onChange={(e) => handleChange('roleType', e.target.value)}
            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-affinicar-primary focus:tw-border-blue-500"
            required
          >
            {Object.entries(ROLE_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Permissions */}
      <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg">
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
            <Shield className="tw-inline tw-w-4 tw-h-4 tw-mr-1" />
            Permissions
          </label>
          <button
            type="button"
            onClick={() => setShowCustomPermissions(!showCustomPermissions)}
            className="tw-text-sm tw-text-affinicar-primary hover:tw-text-blue-700"
          >
            {showCustomPermissions ? 'Masquer' : 'Personnaliser'}
          </button>
        </div>

        {!showCustomPermissions ? (
          <p className="tw-text-sm tw-text-gray-600">
            Permissions par défaut pour le rôle <strong>{ROLE_TYPE_LABELS[formData.roleType]}</strong> ({formData.permissions?.length || 0} permissions)
          </p>
        ) : (
          <div>
            <div className="tw-mb-3">
              <button
                type="button"
                onClick={handleResetPermissions}
                className="tw-text-sm tw-text-gray-600 hover:tw-text-gray-900"
              >
                Réinitialiser aux permissions par défaut
              </button>
            </div>
            <PermissionsSelector
              availablePermissions={availablePermissions}
              selectedPermissions={formData.permissions}
              onChange={handlePermissionsChange}
            />
          </div>
        )}
      </div>

      {/* Boutons */}
      <div className="tw-flex tw-justify-end tw-gap-3 tw-pt-4 tw-border-t">
        <button
          type="button"
          onClick={onCancel}
          className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
          disabled={isLoading}
        >
          Annuler
        </button>
        <button
          type="submit"
          className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-md hover:tw-bg-affinicar-secondary disabled:tw-opacity-50"
          disabled={isLoading || (!isEdit && !formData.societyId)}
        >
          {isLoading ? 'Enregistrement...' : isEdit ? 'Enregistrer' : 'Créer'}
        </button>
      </div>
    </form>
  );
};

